/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module LogsModule */

import { Injectable } from '@angular/core';
import { tap, withLatestFrom } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { DevLoggerService } from 'dev-logger-service';
import { LogsEffectsService } from 'ng/modules/logs/services/logs-effects.service';

import {
    ILogsCombinedRequestUpdatableParams,
    ILogsEndToEndTimingSignpostData,
    ILogsGroupsSignpostData,
    ILogsSignificanceSignpostData,
    ILogsSignpostUpdatableParams,
    ILogsUpdatableParams,
    ILogsWafLatencySignpostData,
    ILogsWafRuleGroupsSignpostData,
    TLogsCombinedRecursiveRequest,
    TLogsPageStateParams,
} from 'ng/modules/logs/logs.types';

import { ILogsSignpostStates, LogsSignpostStore } from 'ng/modules/logs/services/logs-signpost.store';
import { ITrafficLogsStates, TrafficLogsStore } from '../traffic-logs.store';

/**
 * @param selectedSignpostRequestIds - Selected signpost request ids, need it for
 *      cancelling ongoing request while closing signpost.
 */
export interface ITrafficLogsSignpostStates extends ILogsSignpostStates {
    selectedSignpostRequestIds: string[];
}

/** Default state of the Signpost Store */
const initialState: ITrafficLogsSignpostStates = {
    logsSignpostData: null,
    selectedSignpostConfig: null,
    selectedSignpostRequestIds: [],
    isLoading: false,
    errorStates: {
        hasError: false,
        isTimedOut: false,
    },
};

/**
 * @description State and Effect management for LogsSignpostComponent.
 * @author Suraj Kumar, Yogesh Patil
 */
@Injectable()
export abstract class TrafficLogsSignpostStore extends LogsSignpostStore<ITrafficLogsSignpostStates> {
    // ********************************** Selectors **********************************

    /**
     * Selector returning the waf latency signpost.
     */
    public readonly logsWafLatencySignpostData$ = this.select(
        state => state.logsSignpostData as ILogsWafLatencySignpostData,
    );

    /**
     * Selector returning the waf groups signpost.
     */
    public readonly logsWafGroupsSignpostData$ = this.select(
        state => state.logsSignpostData as ILogsWafRuleGroupsSignpostData[],
    );

    /**
     * Selector returning the waf rule signpost.
     */
    public readonly logsWafRuleSignpostData$ = this.select(
        state => state.logsSignpostData as ILogsWafRuleGroupsSignpostData[],
    );

    /**
     * Selector returning the end to end timing signpost.
     */
    public readonly logsEndToEndTimingSignpostData$ = this.select(
        state => state.logsSignpostData as ILogsEndToEndTimingSignpostData,
    );

    /**
     * Selector returning the significance signpost.
     */
    public readonly logsSignificanceSignpostData$ = this.select(
        state => state.logsSignpostData as ILogsSignificanceSignpostData[],
    );

    /**
     * Selector returning the groups signpost.
     */
    public readonly logsGroupsSignpostData$ = this.select(
        state => state.logsSignpostData as ILogsGroupsSignpostData[],
    );

    // ********************************** Effects **********************************

    /**
     * Request selected signpost data and update the state accordingly.
     */
    public readonly getLogSignpostData: (
        signpostParams: ILogsSignpostUpdatableParams,
    ) => Subscription;

    /**
     * Request selected signpost data which depends on response from multiple requests and update
     * state accordingly.
     */
    public readonly getLogSignpostCombinedData: (
        signpostParams: ILogsCombinedRequestUpdatableParams[],
    ) => Subscription;

    // ********************************** Constructor **********************************

    constructor(
        protected readonly trafficLogsStore: TrafficLogsStore<ITrafficLogsStates>,
        logsEffectsService: LogsEffectsService,
        protected readonly devLoggerService: DevLoggerService,
    ) {
        super(initialState, trafficLogsStore, devLoggerService);

        // Initialize request effect
        const requestLogSignpostDataEffect =
            logsEffectsService.createLogsRecursiveRequestEffect(
                this,
                this.startLoading,
                this.stopLoading,
                this.handleApiData,
                this.handleApiError,
                this.handleApiLoopComplete,
            );

        this.getLogSignpostData = this.effect<ILogsSignpostUpdatableParams>(
            signpostParams$ => signpostParams$.pipe(
                withLatestFrom(this.trafficLogsStore.logsPageParams$),
                tap(([signpostParams, logsPageparams]:
                [ILogsSignpostUpdatableParams, TLogsPageStateParams]) => {
                    const apiParams: ILogsUpdatableParams = {
                        ...logsPageparams,
                        ...signpostParams,
                    };

                    this.setLogSelectedSignpostRequestIds([this.logSignpostRequestId]);

                    requestLogSignpostDataEffect({
                        apiParams,
                        config: {
                            requestId: this.logSignpostRequestId,
                        },
                    });
                }),
            ),
        );

        // Initialize combined request effect
        const requestLogSignpostCombinedDataEffect =
            logsEffectsService.createLogsCombinedRecursiveRequestEffect(
                this,
                this.startLoading,
                this.stopLoading,
                this.handleApiData,
                this.handleApiError,
                this.handleApiLoopComplete,
            );

        // Combined requests are sent for 4 signposts: e2e-timing, ssl, policy and waf latency.
        this.getLogSignpostCombinedData = this.effect<ILogsCombinedRequestUpdatableParams[]>(
            signpostParams$ => signpostParams$.pipe(
                withLatestFrom(this.trafficLogsStore.logsPageParams$),
                tap(([signpostParams, logsPageparams]:
                [ILogsCombinedRequestUpdatableParams[], TLogsPageStateParams]) => {
                    const requestIds: string[] = [];
                    const requests = signpostParams.map(
                        (signpostParam: ILogsCombinedRequestUpdatableParams) => {
                            requestIds.push(signpostParam.requestId);

                            return {
                                requestId: signpostParam.requestId,
                                requestParams: {
                                    ...logsPageparams,
                                    ...signpostParam.requestParams,
                                },
                            };
                        },
                    );

                    this.setLogSelectedSignpostRequestIds(requestIds);

                    const combinedRequest: TLogsCombinedRecursiveRequest = {
                        requests,
                    };

                    requestLogSignpostCombinedDataEffect(combinedRequest);
                }),
            ),
        );
    }

    // ********************************** Updaters **********************************

    public readonly setLogSelectedSignpostRequestIds = this.updater(
        (state, selectedSignpostRequestIds: string[]) => ({
            ...state,
            selectedSignpostRequestIds,
        }),
    );

    public override setDefaultState(): void {
        this.setState(initialState);
    }
}
